
/**
 * Auto-backup service for teachers to ensure data persistence across app restarts
 */

import { universalStorage } from './universalStorage';

class TeacherAutoBackupService {
  private backupInterval: NodeJS.Timeout | null = null;
  private readonly BACKUP_INTERVAL = 2 * 60 * 1000; // 2 minutes
  private readonly MAX_BACKUPS = 5;
  private readonly STORAGE_KEY = 'nalc_teachers';

  start(): void {
    console.log('🛡️ Starting teacher auto-backup service...');
    
    // Initial backup validation on startup
    this.validateAndRestore();
    
    // Start periodic backups
    this.backupInterval = setInterval(() => {
      this.createEmergencyBackup();
    }, this.BACKUP_INTERVAL);
    
    // Backup before page unload
    window.addEventListener('beforeunload', () => {
      this.createEmergencyBackup();
    });
  }

  stop(): void {
    if (this.backupInterval) {
      clearInterval(this.backupInterval);
      this.backupInterval = null;
    }
  }

  private async validateAndRestore(): Promise<void> {
    try {
      console.log('🔍 Validating teacher data integrity on startup...');
      
      const teachers = await universalStorage.getItem(this.STORAGE_KEY) || [];
      const teacherArray = Array.isArray(teachers) ? teachers : [];
      
      if (teacherArray.length === 0) {
        console.log('📦 No teachers found, checking for emergency backups...');
        await this.restoreFromEmergencyBackup();
      } else {
        console.log(`✅ Found ${teacherArray.length} teachers, data integrity OK`);
        // Create a fresh backup
        this.createEmergencyBackup();
      }
    } catch (error) {
      console.error('❌ Teacher data validation failed, attempting recovery:', error);
      await this.restoreFromEmergencyBackup();
    }
  }

  private async createEmergencyBackup(): Promise<void> {
    try {
      const teachers = await universalStorage.getItem(this.STORAGE_KEY) || [];
      const teacherArray = Array.isArray(teachers) ? teachers : [];
      
      if (teacherArray.length > 0) {
        const backupKey = `nalc_teacher_emergency_backup_${Date.now()}`;
        const backupData = {
          timestamp: new Date().toISOString(),
          teacherCount: teacherArray.length,
          data: teacherArray
        };
        
        localStorage.setItem(backupKey, JSON.stringify(backupData));
        
        // Clean old backups
        this.cleanOldBackups();
        
        console.log(`🛡️ Teacher emergency backup created: ${teacherArray.length} teachers`);
      }
    } catch (error) {
      console.error('❌ Teacher emergency backup failed:', error);
    }
  }

  private cleanOldBackups(): void {
    try {
      const backupKeys = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('nalc_teacher_emergency_backup_')) {
          backupKeys.push(key);
        }
      }
      
      // Sort by timestamp (newest first)
      backupKeys.sort().reverse();
      
      // Remove old backups beyond the limit
      if (backupKeys.length > this.MAX_BACKUPS) {
        const toRemove = backupKeys.slice(this.MAX_BACKUPS);
        toRemove.forEach(key => localStorage.removeItem(key));
        console.log(`🧹 Cleaned ${toRemove.length} old teacher backups`);
      }
    } catch (error) {
      console.error('❌ Teacher backup cleanup failed:', error);
    }
  }

  private async restoreFromEmergencyBackup(): Promise<void> {
    try {
      console.log('🔄 Attempting to restore from teacher emergency backup...');
      
      const backupKeys = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('nalc_teacher_emergency_backup_')) {
          backupKeys.push(key);
        }
      }
      
      if (backupKeys.length === 0) {
        console.log('❌ No teacher emergency backups found');
        return;
      }
      
      // Get the most recent backup
      const latestBackupKey = backupKeys.sort().reverse()[0];
      const backupData = localStorage.getItem(latestBackupKey);
      
      if (backupData) {
        const backup = JSON.parse(backupData);
        await universalStorage.setItem(this.STORAGE_KEY, backup.data);
        console.log(`✅ Restored ${backup.teacherCount} teachers from emergency backup`);
      }
    } catch (error) {
      console.error('❌ Teacher emergency restore failed:', error);
    }
  }

  async getBackupInfo(): Promise<{
    totalBackups: number;
    lastBackup: string;
    totalTeachersInLatest: number;
  }> {
    try {
      const backupKeys = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('nalc_teacher_emergency_backup_')) {
          backupKeys.push(key);
        }
      }
      
      if (backupKeys.length === 0) {
        return {
          totalBackups: 0,
          lastBackup: 'Never',
          totalTeachersInLatest: 0
        };
      }
      
      const latestBackupKey = backupKeys.sort().reverse()[0];
      const backupData = localStorage.getItem(latestBackupKey);
      const backup = backupData ? JSON.parse(backupData) : null;
      
      return {
        totalBackups: backupKeys.length,
        lastBackup: backup?.timestamp || 'Unknown',
        totalTeachersInLatest: backup?.teacherCount || 0
      };
    } catch (error) {
      console.error('Error getting teacher backup info:', error);
      return {
        totalBackups: 0,
        lastBackup: 'Error',
        totalTeachersInLatest: 0
      };
    }
  }
}

export const teacherAutoBackupService = new TeacherAutoBackupService();
